import type { ReactNode } from 'react';
import { AlertCircle, Inbox, RefreshCw, SearchX, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CopyableId } from '@/components/CopyableId';
import { describeError, isApiError } from '@/lib/apiError';
import { ErrorCode } from '@/types/api';
import { cn } from '@/lib/cn';

// -----------------------------------------------------------------------------
// Loading / empty / error.
//
// Three states, three components, never collapsed into one. "No results found"
// rendered because a request failed is a lie, and it is the specific lie that
// makes an operator conclude a queue is clear when it is actually unreachable.
// -----------------------------------------------------------------------------

/** Rows of shimmering cells, sized to the table that is loading. */
export function TableSkeleton({
  rows = 8,
  columns = 5,
  className,
}: {
  rows?: number;
  columns?: number;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn('divide-y divide-border', className)}
      aria-busy="true"
      aria-label="Loading results"
    >
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex h-row items-center gap-4 px-3">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <Skeleton
              key={columnIndex}
              className={cn('h-3.5', columnIndex === 0 ? 'w-40' : 'flex-1 max-w-[140px]')}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function CardSkeleton({ className }: { className?: string }): JSX.Element {
  return (
    <div
      className={cn('rounded-lg border border-border bg-card p-4 shadow-card', className)}
      aria-busy="true"
    >
      <Skeleton className="h-3 w-24" />
      <Skeleton className="mt-3 h-7 w-32" />
      <Skeleton className="mt-3 h-3 w-20" />
    </div>
  );
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
}: {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn('flex flex-col items-center justify-center gap-2 px-6 py-14 text-center', className)}
    >
      <div className="mb-1 flex size-10 items-center justify-center rounded-lg bg-lavender-tint text-primary">
        {icon ?? <Inbox className="size-5" aria-hidden />}
      </div>
      <p className="text-md font-semibold text-foreground">{title}</p>
      {description ? (
        <p className="max-w-sm text-base text-muted-foreground">{description}</p>
      ) : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

/** The empty state for a search that legitimately matched nothing. */
export function NoResultsState({
  query,
  onClear,
}: {
  query?: string;
  onClear?: () => void;
}): JSX.Element {
  return (
    <EmptyState
      icon={<SearchX className="size-5" aria-hidden />}
      title="No matches"
      description={
        query ? `Nothing matched “${query}”. Try a different term or clear the filters.` : 'No records match the current filters.'
      }
      action={
        onClear ? (
          <Button variant="outline" size="sm" onClick={onClear}>
            Clear filters
          </Button>
        ) : undefined
      }
    />
  );
}

/**
 * A failed request.
 *
 * `FORBIDDEN` gets its own treatment and no retry button: the request will fail
 * identically every time, and offering "Try again" on a permission error trains
 * people to hammer an endpoint that is telling them no.
 */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}): JSX.Element {
  const { title, description } = describeError(error);
  const forbidden =
    isApiError(error) &&
    (error.code === ErrorCode.FORBIDDEN || error.code === ErrorCode.ROLE_MISMATCH);
  const requestId = isApiError(error) ? error.requestId : null;
  const retryable = !forbidden && onRetry;

  return (
    <div
      role="alert"
      className={cn('flex flex-col items-center justify-center gap-2 px-6 py-14 text-center', className)}
    >
      <div
        className={cn(
          'mb-1 flex size-10 items-center justify-center rounded-lg',
          forbidden ? 'bg-warning-soft text-warning' : 'bg-danger-soft text-destructive',
        )}
      >
        {forbidden ? (
          <ShieldAlert className="size-5" aria-hidden />
        ) : (
          <AlertCircle className="size-5" aria-hidden />
        )}
      </div>
      <p className="text-md font-semibold text-foreground">{title}</p>
      <p className="max-w-md text-base text-muted-foreground">{description}</p>

      {retryable ? (
        <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          <RefreshCw aria-hidden />
          Try again
        </Button>
      ) : null}

      {requestId ? <CopyableId className="mt-3" label="Request id" value={requestId} /> : null}
    </div>
  );
}

/**
 * A single component that resolves the three states in the right order.
 *
 * Error is checked BEFORE empty. Reversing them is exactly how a failed request
 * ends up rendering "nothing here".
 */
export function QueryStates({
  isLoading,
  error,
  isEmpty,
  onRetry,
  loadingFallback,
  emptyFallback,
  children,
}: {
  isLoading: boolean;
  error: unknown;
  isEmpty: boolean;
  onRetry?: () => void;
  loadingFallback: ReactNode;
  emptyFallback: ReactNode;
  children: ReactNode;
}): JSX.Element {
  if (isLoading) return <>{loadingFallback}</>;
  if (error) return <ErrorState error={error} {...(onRetry ? { onRetry } : {})} />;
  if (isEmpty) return <>{emptyFallback}</>;
  return <>{children}</>;
}
